// src/components/Utils/MDtoHTML.js

/**
 * Markdown을 HTML로 변환하는 유틸리티 함수
 * @param {string} markdown - 변환할 마크다운 텍스트
 * @returns {string} - 변환된 HTML 문자열
 */
export const convertMarkdownToHTML = (markdown) => {
  let html = markdown;

  // HTML 특수문자 이스케이프 함수
  const escapeHtml = (text) => {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, m => map[m]);
  };

  // 코드 블록 (먼저 처리하여 다른 마크다운 문법이 적용되지 않도록)
  html = html.replace(/```(\w+)?\n([\s\S]*?)```/g, (match, lang, code) => {
    return `<pre><code class="language-${lang || 'plaintext'}">${escapeHtml(code.trim())}</code></pre>`;
  });

  // 인라인 코드
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

  // 헤더 (H1-H6)
  html = html.replace(/^#{6}\s+(.+)$/gm, '<h6>$1</h6>');
  html = html.replace(/^#{5}\s+(.+)$/gm, '<h5>$1</h5>');
  html = html.replace(/^#{4}\s+(.+)$/gm, '<h4>$1</h4>');
  html = html.replace(/^#{3}\s+(.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^#{2}\s+(.+)$/gm, '<h2>$1</h2>');
  html = html.replace(/^#\s+(.+)$/gm, '<h1>$1</h1>');

  // 수평선
  html = html.replace(/^-{3,}$/gm, '<hr>');
  html = html.replace(/^\*{3,}$/gm, '<hr>');
  html = html.replace(/^_{3,}$/gm, '<hr>');

  // 굵은 글씨
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');

  // 기울임
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  // html = html.replace(/_(.+?)_/g, '<em>$1</em>');

  // 인용문
  html = html.replace(/^>\s+(.+)$/gm, '<blockquote>$1</blockquote>');

  // 이미지 (링크보다 먼저 처리)
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" style="max-width: 100%; height: auto; border-radius: 8px; margin: 15px 0;">');

  // 링크
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank">$1</a>');

  // 순서 없는 리스트
  const lines = html.split('\n');
  let inList = false;
  let processedLines = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const listMatch = line.match(/^[\s]*[-*+]\s+(.+)$/);

    if (listMatch) {
      if (!inList) {
        processedLines.push('<ul>');
        inList = true;
      }
      processedLines.push(`<li>${listMatch[1]}</li>`);
    } else {
      if (inList) {
        processedLines.push('</ul>');
        inList = false;
      }
      processedLines.push(line);
    }
  }

  if (inList) {
    processedLines.push('</ul>');
  }

  html = processedLines.join('\n');

  // 순서 있는 리스트
  lines.length = 0;
  processedLines.length = 0;
  const newLines = html.split('\n');
  let inOrderedList = false;

  for (let i = 0; i < newLines.length; i++) {
    const line = newLines[i];
    const orderedListMatch = line.match(/^[\s]*\d+\.\s+(.+)$/);

    if (orderedListMatch) {
      if (!inOrderedList) {
        processedLines.push('<ol>');
        inOrderedList = true;
      }
      processedLines.push(`<li>${orderedListMatch[1]}</li>`);
    } else {
      if (inOrderedList) {
        processedLines.push('</ol>');
        inOrderedList = false;
      }
      processedLines.push(line);
    }
  }

  if (inOrderedList) {
    processedLines.push('</ol>');
  }

  html = processedLines.join('\n');

  // 단락 처리 (빈 줄로 구분된 텍스트를 <p> 태그로 감싸기)
  html = html.split('\n\n').map(para => {
    para = para.trim();
    if (!para) return '';

    // 이미 HTML 태그로 시작하는 경우 그대로 반환
    if (para.match(/^<(h[1-6]|ul|ol|pre|blockquote|hr|img|p|div|table)/i)) {
      return para;
    }

    // 일반 텍스트는 <p> 태그로 감싸기
    return `<p>${para.replace(/\n/g, '<br>')}</p>`;
  }).join('\n\n');

  return html;
};

/**
 * Markdown을 완전한 HTML 문서로 변환
 * @param {string} markdown - 변환할 마크다운 텍스트
 * @param {string} title - HTML 문서 제목 (기본값: '변환된 문서')
 * @returns {string} - 완전한 HTML 문서
 */
export const convertToFullHTML = (markdown, title = '변환된 문서') => {
  const htmlContent = convertMarkdownToHTML(markdown);

  return `<!DOCTYPE html>
<html lang="ko">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${title}</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }

        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
            line-height: 1.7;
            color: #333;
            background: #f8f9fa;
            padding: 40px 20px;
        }

        .container {
            max-width: 900px;
            margin: 0 auto;
            background: white;
            padding: 60px;
            border-radius: 12px;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        }

        /* 제목 스타일 */
        h1 {
            font-size: 2.5rem;
            font-weight: 700;
            color: #1a202c;
            margin: 1.5rem 0 1rem;
            border-bottom: 3px solid #667eea;
            padding-bottom: 0.8rem;
        }

        h2 {
            font-size: 2rem;
            font-weight: 600;
            color: #2d3748;
            margin: 2rem 0 1rem;
            border-bottom: 2px solid #e2e8f0;
            padding-bottom: 0.5rem;
        }

        h3 {
            font-size: 1.5rem;
            font-weight: 600;
            color: #4a5568;
            margin: 1.5rem 0 0.8rem;
        }

        h4 {
            font-size: 1.25rem;
            font-weight: 600;
            color: #4a5568;
            margin: 1.2rem 0 0.6rem;
        }

        h5 {
            font-size: 1.1rem;
            font-weight: 600;
            color: #4a5568;
            margin: 1rem 0 0.5rem;
        }

        h6 {
            font-size: 1rem;
            font-weight: 600;
            color: #718096;
            margin: 1rem 0 0.5rem;
        }

        /* 본문 텍스트 */
        p {
            margin: 1rem 0;
            line-height: 1.8;
        }

        /* 링크 */
        a {
            color: #667eea;
            text-decoration: none;
            font-weight: 500;
            border-bottom: 1px solid transparent;
            transition: all 0.2s ease;
        }

        a:hover {
            color: #764ba2;
            border-bottom-color: #764ba2;
        }

        /* 강조 */
        strong {
            font-weight: 700;
            color: #1a202c;
        }

        em {
            font-style: italic;
            color: #4a5568;
        }

        /* 코드 */
        code {
            background: #f7fafc;
            padding: 3px 8px;
            border-radius: 4px;
            font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
            font-size: 0.9em;
            color: #d73a49;
            border: 1px solid #e2e8f0;
        }

        pre {
            background: #2d3748;
            color: #e2e8f0;
            padding: 20px;
            border-radius: 8px;
            overflow-x: auto;
            margin: 1.5rem 0;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        pre code {
            background: none;
            padding: 0;
            border: none;
            color: inherit;
            font-size: 0.95rem;
        }

        /* 인용문 */
        blockquote {
            border-left: 4px solid #667eea;
            padding: 12px 20px;
            margin: 1.5rem 0;
            background: #f7fafc;
            border-radius: 0 8px 8px 0;
            color: #4a5568;
            font-style: italic;
        }

        blockquote p {
            margin: 0.5rem 0;
        }

        /* 리스트 */
        ul, ol {
            margin: 1rem 0;
            padding-left: 2rem;
        }

        ul {
            list-style-type: disc;
        }

        ol {
            list-style-type: decimal;
        }

        li {
            margin: 0.5rem 0;
            line-height: 1.8;
        }

        li::marker {
            color: #667eea;
            font-weight: bold;
        }

        /* 수평선 */
        hr {
            border: none;
            height: 2px;
            background: linear-gradient(to right, transparent, #cbd5e1, transparent);
            margin: 2.5rem 0;
        }

        /* 이미지 */
        img {
            max-width: 100%;
            height: auto;
            border-radius: 8px;
            margin: 1.5rem 0;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        /* 반응형 디자인 */
        @media (max-width: 768px) {
            .container {
                padding: 30px 20px;
            }

            h1 {
                font-size: 2rem;
            }

            h2 {
                font-size: 1.6rem;
            }

            h3 {
                font-size: 1.3rem;
            }

            body {
                padding: 20px 10px;
            }
        }

        /* 프린트 스타일 */
        @media print {
            body {
                background: white;
                padding: 0;
            }

            .container {
                box-shadow: none;
                padding: 20px;
            }

            a {
                color: #000;
                text-decoration: underline;
            }
        }
    </style>
</head>
<body>
    <div class="container">
${htmlContent}
    </div>
</body>
</html>`;
};

/**
 * HTML 파일을 다운로드하는 함수
 * @param {string} markdown - 변환할 마크다운 텍스트
 * @param {string} filename - 저장할 파일명 (기본값: 'document.html')
 */
export const downloadAsHTML = (markdown, filename = 'document.html') => {
  const htmlContent = convertToFullHTML(markdown);
  const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // 메모리 정리
  URL.revokeObjectURL(url);
};

/**
 * HTML을 Markdown으로 변환하는 유틸리티 함수 (간단한 구현)
 * @param {string} html - 변환할 HTML 텍스트
 * @returns {string} - 변환된 Markdown 문자열
 */
export const convertHTMLToMarkdown = (html) => {
  let markdown = html;

  // 줄바꿈 처리
  markdown = markdown.replace(/<br\s*\/?>/gi, '\n');
  markdown = markdown.replace(/<\/p>/gi, '\n\n');
  markdown = markdown.replace(/<\/div>/gi, '\n');
  markdown = markdown.replace(/<\/h[1-6]>/gi, '\n\n');
  markdown = markdown.replace(/<\/li>/gi, '\n');
  markdown = markdown.replace(/<\/ul>/gi, '\n');
  markdown = markdown.replace(/<\/ol>/gi, '\n');
  markdown = markdown.replace(/<\/tr>/gi, '\n');

  // 헤더
  markdown = markdown.replace(/<h1[^>]*>(.*?)<\/h1>/gi, '# $1');
  markdown = markdown.replace(/<h2[^>]*>(.*?)<\/h2>/gi, '## $1');
  markdown = markdown.replace(/<h3[^>]*>(.*?)<\/h3>/gi, '### $1');
  markdown = markdown.replace(/<h4[^>]*>(.*?)<\/h4>/gi, '#### $1');
  markdown = markdown.replace(/<h5[^>]*>(.*?)<\/h5>/gi, '##### $1');
  markdown = markdown.replace(/<h6[^>]*>(.*?)<\/h6>/gi, '###### $1');

  // 굵게/기울임
  markdown = markdown.replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**');
  markdown = markdown.replace(/<b[^>]*>(.*?)<\/b>/gi, '**$1**');
  markdown = markdown.replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*');
  markdown = markdown.replace(/<i[^>]*>(.*?)<\/i>/gi, '*$1*');

  // 링크
  markdown = markdown.replace(/<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gi, '[$2]($1)');

  // 이미지
  markdown = markdown.replace(/<img[^>]*src="([^"]*)"[^>]*alt="([^"]*)"[^>]*>/gi, '![$2]($1)');

  // 리스트
  markdown = markdown.replace(/<li[^>]*>(.*?)<\/li>/gi, '- $1'); // 순서 없는 리스트로 통일 (간단 구현)

  // 인용문
  markdown = markdown.replace(/<blockquote[^>]*>(.*?)<\/blockquote>/gi, '> $1');

  // 코드
  markdown = markdown.replace(/<pre><code[^>]*>(.*?)<\/code><\/pre>/gis, '```\n$1\n```');
  markdown = markdown.replace(/<code[^>]*>(.*?)<\/code>/gi, '`$1`');

  // HTML 태그 제거 (남은 태그들)
  markdown = markdown.replace(/<[^>]+>/g, '');

  // HTML 엔티티 디코딩
  const entities = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#039;': "'",
    '&nbsp;': ' '
  };
  markdown = markdown.replace(/&[a-z0-9]+;/g, m => entities[m] || m);

  return markdown.trim();
};

/**
 * HTML 태그를 제거하고 텍스트만 추출하는 함수
 * @param {string} html - HTML 텍스트
 * @returns {string} - 순수 텍스트
 */
export const stripHTMLTags = (html) => {
  let text = html;
  text = text.replace(/<br\s*\/?>/gi, '\n');
  text = text.replace(/<\/p>/gi, '\n\n');
  text = text.replace(/<[^>]+>/g, ''); // 태그 제거

  // 엔티티 디코딩
  const entities = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#039;': "'",
    '&nbsp;': ' '
  };
  text = text.replace(/&[a-z0-9]+;/g, m => entities[m] || m);

  return text.trim();
};

export default {
  convertMarkdownToHTML,
  convertToFullHTML,
  downloadAsHTML,
  convertHTMLToMarkdown,
  stripHTMLTags
};